import { useMemo, useState } from 'react'
import type { SupplierItem } from '@fitting-room/contracts'
import { List, Pencil } from 'lucide-react'
import { useSupplierItems } from '../../../lib/adminQueries'
import { BOUTIQUE_LOCALE } from '../../../lib/boutique'
import { formatDay } from '../../../lib/format'
import { useStaffToken } from '../../../lib/staffToken'

/** A Jovita item in one line, for a select: who and what first, then Jovita's numbers. */
export function jovitaItemLabel(item: SupplierItem): string {
  return [
    item.brideName,
    item.style ?? item.pbfNo ?? 'No style',
    item.size ? `size ${item.size}` : null,
    item.partnerRef ? `PO# ${item.partnerRef}` : null,
    item.weddingDate ? `wedding ${formatDay(item.weddingDate, BOUTIQUE_LOCALE)}` : null,
    item.factoryNo ? `factory ${item.factoryNo}` : null,
    `Jovita #${item.itemNo ?? '—'}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The supplier side of a special-order line: its item from Jovita, picked
 * from the items nothing is linked to yet — this line's likeliest first —
 * with a pencil beside it to type the supplier's number instead, for an
 * order Jovita doesn't list or a supplier that isn't Jovita.
 *
 * Picking an item is linking it (the caller does that on Save): the line
 * then follows Jovita — the factory number, the ETA, shipped and arrived —
 * as it does from the Jovita orders page. Without Jovita connected it is the
 * typed field alone.
 */
export function JovitaItemField({
  lineId,
  itemId,
  typed,
  onPick,
  onType,
  typedLabel,
  typedHint,
  autoFocus,
  startTyping = false,
}: {
  readonly lineId: string
  readonly itemId: string
  readonly typed: string
  readonly onPick: (item: SupplierItem | null) => void
  readonly onType: (text: string) => void
  readonly typedLabel: string
  readonly typedHint?: string | undefined
  readonly autoFocus?: boolean
  /** Start on the typed field: the line already has a number somebody typed. */
  readonly startTyping?: boolean
}) {
  const { token } = useStaffToken()
  const items = useSupplierItems(token)
  const [typing, setTyping] = useState(startTyping)
  const noList = items.isError
  const manual = typing || noList

  const { likely, others } = useMemo(() => {
    const open = (items.data?.items ?? []).filter((row) => !row.linked)
    const ranked = open
      .map((row) => ({ row, score: row.suggestions.find((s) => s.lineId === lineId)?.score ?? 0 }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.row)
    return { likely: ranked, others: open.filter((row) => !ranked.includes(row)) }
  }, [items.data, lineId])

  const choose = (id: string) => {
    const row = [...likely, ...others].find((r) => r.item.id === id)
    onPick(row ? row.item : null)
  }

  return (
    <div className="mb-3 flex flex-col gap-1">
      <label className="st-eyebrow" htmlFor={manual ? `typed-${lineId}` : `jovita-${lineId}`}>
        {manual ? typedLabel : 'Jovita item'}
      </label>
      <div className="flex items-center gap-1.5">
        {manual ? (
          <input
            id={`typed-${lineId}`}
            className="st-input"
            maxLength={64}
            autoComplete="off"
            autoFocus={autoFocus}
            value={typed}
            onChange={(event) => onType(event.target.value)}
          />
        ) : (
          <select
            id={`jovita-${lineId}`}
            className="st-select min-w-0 flex-1"
            value={itemId}
            disabled={items.isPending}
            onChange={(event) => choose(event.target.value)}
            data-testid="jovita-item-pick"
          >
            <option value="">{items.isPending ? 'Loading Jovita items…' : likely.length > 0 ? 'Choose a Jovita item…' : 'No likely item — choose one…'}</option>
            {likely.length > 0 ? (
              <optgroup label="Likely">
                {likely.map((row) => (
                  <option key={row.item.id} value={row.item.id}>
                    {jovitaItemLabel(row.item)}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {others.length > 0 ? (
              <optgroup label="Other Jovita items">
                {others.map((row) => (
                  <option key={row.item.id} value={row.item.id}>
                    {jovitaItemLabel(row.item)}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        )}
        {noList ? null : manual ? (
          <button type="button" className="st-btn-ghost shrink-0 px-2" aria-label="Choose from Jovita instead" title="Choose from Jovita" onClick={() => setTyping(false)}>
            <List size={14} aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            className="st-btn-ghost shrink-0 px-2"
            aria-label="Type the supplier order number instead"
            title="Type the number"
            onClick={() => {
              setTyping(true)
              onPick(null)
            }}
          >
            <Pencil size={14} aria-hidden />
          </button>
        )}
      </div>
      <span className="st-meta text-xs">
        {manual
          ? noList
            ? `${items.error?.code === 'supplier_not_connected' ? 'Jovita isn’t connected, so type it in.' : 'Couldn’t reach Jovita just now, so type it in.'}${typedHint ? ` ${typedHint}` : ''}`
            : (typedHint ?? 'For an order Jovita doesn’t list, or another supplier.')
          : itemId
            ? 'Saving links the line to this item: the factory number, the ETA, shipped and arrived follow Jovita from then on.'
            : 'Items ordered from Jovita that no line has yet. Not there? Press the pencil to type the number.'}
      </span>
    </div>
  )
}
